import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, Typography, Button, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useRouteMatch } from 'react-router';

import { useProjectApi, useIsicApi } from 'src/services';
import { TitleContainer, ContentContainer, DialogWrap } from 'src/components';
import { upperCaseOnlyFirstLetter as ucofl } from 'src/utils/formatters';
import { LoadingBox } from 'src/scenes/Project/scenes/Dashboard/components';
import { IsicSelectionDialog } from 'src/scenes/Project/scenes/PressuresAndDependencies/components';
import { LabelChip } from 'src/components';
import { useFetchStatus } from 'src/hooks';


const useStyles = makeStyles(() => ({
  isicLabel: {
    width: '56px',
  },
  loadingContainer: {
    minWidth: '500px',
  },
}));


const IsicContainer = props => {

  const classes = useStyles();
  const match = useRouteMatch();
  const projectApi = useProjectApi();
  const isicApi = useIsicApi();

  let { projectId, siteId } = match.params;
  const isSite = Boolean(siteId);
  projectId = isSite ? siteId : projectId;

  const [ isic, setIsic ] = useState();

  const [ dialog, setDialog ] = useState({ isOpen: false, type: '' });
  const closeDialog = () => setDialog({ isOpen: false, type: '' });
  const openDialog = (type, data = {}) => setDialog({ isOpen: true, type, data });

  const openIsicDialog = () => openDialog('isic-selection');


  const getIsic = async () => {
    const isic = await projectApi.getIsic(projectId);
    setIsic(isic);
  };

  const { handleConfirm: fetchIsic, isLoading: isLoadingIsic, isSuccess: isSuccessIsic } = useFetchStatus({
    confirmFn: getIsic,
  });

  useEffect(() => {
    fetchIsic();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getDialogType = () => {
    switch (dialog.type) {
      case 'isic-selection':
        return <IsicSelectionDialog actions={ {
          closeDialog,
          getIsicOptions: isicsToFetch => isicApi.getOptions(isicsToFetch),
          saveIsic,
        }} />;
      default:
        break;
    }
  };

  const saveIsic = async isicData => {
    await isicApi.saveIsic({ ...isicData, projectId });
    await getIsic();
  };

  const hasIsic = Boolean(isic?.id);

  return (
    <Grid container>
      <TitleContainer
        maxWidth="xl"
        project={props.project}
        breadcrumbs={[ 'projects', 'project', { name: 'Clasificación' } ]}>
        Clasificación ISIC
      </TitleContainer>
      <ContentContainer maxWidth="xl">
        <Box my={2} />
        <Box p={2}>
          {isLoadingIsic &&
            <Grid container direction="column" alignContent="center">
              <Grid item>
                <Paper elevation={1}>
                  <Box className={classes.loadingContainer}>
                    <LoadingBox label="ISIC" />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          }
          {(isSuccessIsic && hasIsic) &&
            <Grid container direction="column" alignItems="flex-start">
              <Grid item>
                <Paper elevation={1}>
                  <Box p={2}>
                    <Box display="flex" alignItems="center" visibility={isic.uniqueCode ? 'visible' : 'hidden'}>
                      <Typography className={classes.isicLabel} variant="body2" color="textSecondary" gutterBottom>
                        Código:
                      </Typography>
                      <LabelChip variant="outlined" label={ucofl(isic.uniqueCode)} color="default" />
                    </Box>
                    <Box display="flex" alignItems="center" visibility={isic.sectionEsp ? 'visible' : 'hidden'}>
                      <Typography className={classes.isicLabel} variant="body2" color="textSecondary" gutterBottom>
                        Sección:
                      </Typography>
                      <LabelChip variant="outlined" label={ucofl(isic.sectionEsp)} color="default" />
                    </Box>
                    <Box display="flex" alignItems="center" visibility={isic.divisionEsp ? 'visible' : 'hidden'}>
                      <Typography className={classes.isicLabel} variant="body2" color="textSecondary" gutterBottom>
                        División:
                      </Typography>
                      <LabelChip variant="outlined" label={ucofl(isic.divisionEsp)} color="default" />
                    </Box>
                    <Box display="flex" alignItems="center" visibility={isic.groupEsp ? 'visible' : 'hidden'}>
                      <Typography className={classes.isicLabel} variant="body2" color="textSecondary" gutterBottom>
                        Grupo:
                      </Typography>
                      <LabelChip variant="outlined" label={ucofl(isic.groupEsp)} color="default" />
                    </Box>
                    <Box display="flex" alignItems="center" visibility={isic.classEsp ? 'visible' : 'hidden'}>
                      <Typography className={classes.isicLabel} variant="body2" color="textSecondary" gutterBottom>
                        Clase:
                      </Typography>
                      <LabelChip variant="outlined" label={ucofl(isic.classEsp)} color="default" />
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item>
                <Box mt={1}>
                  <Button variant="contained" color="primary" onClick={openIsicDialog}>
                    Modificar ISIC
                  </Button>
                </Box>
              </Grid>
            </Grid>
          }
          {(isSuccessIsic && !hasIsic) &&
            <Grid container direction="column" spacing={0} alignItems="center">
              <Grid item>
                <Typography variant="h6" color="textSecondary">
                  No se ha seleccionado una clasificación ISIC.
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Para elegir una haz clic en el botón &quot;&quot;Ingresar clasificación ISIC&quot;&quot;.
                </Typography>
              </Grid>
              <Box my={1} />
              <Grid item>
                <Button variant="contained" color="primary" onClick={openIsicDialog}>
                  Ingresar clasificación ISIC
                </Button>
              </Grid>
            </Grid>
          }
        </Box>
      </ContentContainer>
      <DialogWrap maxWidth='sm' fullWidth onClose={closeDialog} aria-labelledby='form-dialog-title' open={dialog.isOpen}>
        {getDialogType()}
      </DialogWrap>
    </Grid>
  );
};

IsicContainer.propTypes = {
  project: PropTypes.shape({
    siteName: PropTypes.string,
    siteId: PropTypes.number,
    name: PropTypes.string,
    id: PropTypes.number,
  }),
};


export {
  IsicContainer,
};
